/**
 * Shared MessageAttachments Component
 * Renders file attachments in support ticket messages with:
 * - Image preview for image files (jpg, jpeg, png, gif, webp)
 * - Download link for other files
 * - Hover download button on images
 */

import { Download, FileText, Image as ImageIcon, File } from 'lucide-react';

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Get appropriate icon based on file extension
const getFileIcon = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return ImageIcon;
  if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) return FileText;
  return File;
};

// Check if file is an image
const isImageFile = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
};

/**
 * MessageAttachments Component
 * @param {Array} attachments - Array of attachment objects with {url, filename}
 */
export function MessageAttachments({ attachments }) {
  if (!attachments?.length) return null;

  return (
    <div className="mt-2 space-y-2" data-testid="message-attachments">
      {attachments.map((att) => {
        const AttIcon = getFileIcon(att.filename);
        const attachmentUrl = `${BASE_URL}${att.url}`;

        if (isImageFile(att.filename)) {
          // Show image preview for image files
          return (
            <div key={att.url || att.filename} className="relative group">
              <a
                href={attachmentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <img
                  src={attachmentUrl}
                  alt={att.filename}
                  data-testid="attachment-image"
                  className="max-w-full max-h-48 rounded-lg border shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) {
                      e.target.nextSibling.style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden items-center gap-2 p-2 bg-white/50 rounded border text-xs">
                  <AttIcon className="h-4 w-4 text-slate-500" />
                  <span className="truncate flex-1">{att.filename}</span>
                  <Download className="h-3 w-3 text-slate-400" />
                </div>
              </a>
              <div className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <a
                  href={attachmentUrl}
                  download={att.filename}
                  className="bg-black/50 text-white p-1 rounded"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Download className="h-3 w-3" />
                </a>
              </div>
            </div>
          );
        }

        // Regular file link for non-image files
        return (
          <a
            key={att.url || att.filename}
            href={attachmentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-2 bg-white/50 rounded border text-xs hover:bg-white transition-colors"
            data-testid="attachment-file"
          >
            <AttIcon className="h-4 w-4 text-slate-500" />
            <span className="truncate flex-1">{att.filename}</span>
            <Download className="h-3 w-3 text-slate-400" />
          </a>
        );
      })}
    </div>
  );
}

// Export helpers for use in attachment preview during upload
export { getFileIcon, isImageFile };
